// src/features/portfolio-optimization-tools/multi-asset-portfolio-optimizer/components/MacroAllocationDonut.tsx
//
// Tier-2 Black-Litterman macro allocation across sleeves as a donut + legend.
// Matches the design language of the existing portfolio-optimization charts
// (dark-mode aware tooltip, recharts donut, slate scaffolding).

import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import { sleeveColor, sleeveLabel } from './sleeveColors';

export interface MacroSlice {
    /** Sleeve id, e.g. 'equities'. */
    name: string;
    /** Percent of the risky book (0..100). */
    value: number;
}

interface MacroAllocationDonutProps {
    data: MacroSlice[];
}

const DonutTooltip = ({
    active,
    payload,
}: {
    active?: boolean;
    payload?: { payload: MacroSlice }[];
}) => {
    if (!active || !payload?.length) return null;
    const slice = payload[0].payload;
    return (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-xs shadow-lg">
            <p className="font-bold text-slate-900 dark:text-white">
                {sleeveLabel(slice.name)}
            </p>
            <p className="font-mono text-slate-600 dark:text-slate-300">
                {slice.value.toFixed(1)}%
            </p>
        </div>
    );
};

export const MacroAllocationDonut = ({ data }: MacroAllocationDonutProps) => {
    if (!data.length) {
        return <p className="muted text-sm">No macro allocation to display.</p>;
    }

    return (
        <div className="flex flex-col">
            <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={data}
                            cx="50%"
                            cy="50%"
                            innerRadius={62}
                            outerRadius={92}
                            paddingAngle={2}
                            dataKey="value"
                            stroke="none"
                        >
                            {data.map((slice) => (
                                <Cell
                                    key={slice.name}
                                    fill={sleeveColor(slice.name)}
                                />
                            ))}
                        </Pie>
                        <Tooltip content={<DonutTooltip />} />
                    </PieChart>
                </ResponsiveContainer>
            </div>

            <div className="mt-4 flex flex-wrap gap-x-3 gap-y-2 justify-center">
                {data.map((slice) => (
                    <div
                        key={slice.name}
                        className="flex items-center gap-1.5 text-xs font-semibold text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800/60 px-2 py-1 rounded-md border border-slate-100 dark:border-slate-700"
                    >
                        <span
                            className="w-2.5 h-2.5 rounded-full shrink-0"
                            style={{ backgroundColor: sleeveColor(slice.name) }}
                        />
                        {sleeveLabel(slice.name)}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default MacroAllocationDonut;
